import { computed, Directive, input } from '@angular/core';

import { buttonEnumInputTransformer, ButtonEnumInputType } from './button-common-transformers';

export enum CommonButtonIconSize {
	XS = 'XS',
	S = 'S',
	M = 'M',
	L = 'L',
}

export enum CommonButtonIconState {
	DEFAULT = 'DEFAULT',
	SELECTED = 'SELECTED',
	DISABLED = 'DISABLED',
}

const CLASSES_BY_SIZE_MAP: Record<CommonButtonIconSize, string[]> = {
	[CommonButtonIconSize.XS]: ['br-0t', 'p-0h', 'fs-icon-small'],
	[CommonButtonIconSize.S]: ['br-1', 'p-0t', 'fs-icon-small'],
	[CommonButtonIconSize.M]: ['br-1', 'p-0h', 'fs-icon'],
	[CommonButtonIconSize.L]: ['br-1', 'p-1', 'fs-icon'],
};

const CLASSES_BY_STATE_MAP: Record<CommonButtonIconState, string[]> = {
	[CommonButtonIconState.DEFAULT]: ['fc-400', 'bg-standard-hover-on-hover', 'fc-800-on-hover'],
	[CommonButtonIconState.SELECTED]: ['fc-primary', 'bg-primary-20'],
	[CommonButtonIconState.DISABLED]: ['fc-200', 'bg-560-as-fc'],
};

@Directive({
	selector: '[commonButtonIcon]',
	standalone: true,
	host: {
		class: 'fl fl-ai-c fl-jc-fs',
		'[class]': '$classes()',
		'[attr.data-size]': '$size()',
		'[attr.data-state]': '$state()',
		'[attr.disabled]': '$disabled() ? true : null',
	},
})
export class CommonButtonIconDirective {
	public readonly $size = input<CommonButtonIconSize, ButtonEnumInputType<CommonButtonIconSize>>(
		CommonButtonIconSize.M,
		{
			alias: 'size',
			transform: (value) =>
				buttonEnumInputTransformer(
					value,
					Object.values(CommonButtonIconSize),
					CommonButtonIconSize.M
				),
		}
	);

	public readonly $state = input<
		CommonButtonIconState,
		ButtonEnumInputType<CommonButtonIconState>
	>(CommonButtonIconState.DEFAULT, {
		alias: 'state',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonIconState),
				CommonButtonIconState.DEFAULT
			),
	});

	protected readonly $classes = computed(() => {
		const size = this.$size();
		const state = this.$state();

		const classesBySize = CLASSES_BY_SIZE_MAP[size] ?? [];
		const classesByState = CLASSES_BY_STATE_MAP[state] ?? [];

		const classes = [...classesBySize, ...classesByState];

		return classes.join(' ');
	});

	protected readonly $disabled = computed(() => {
		const state = this.$state();
		return state === CommonButtonIconState.DISABLED;
	});
}
